import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook, FaWhatsapp } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, facebookProvider, googleProvider } from "../../configs/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  getRedirectResult,
  sendPasswordResetEmail,
  signOut,
  UserCredential,
} from "firebase/auth";
import {
  createUserProfile,
  claimPhoneNumberViaBackend,
} from "../../utils/firebaseHelpers";
import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import {
  isDuplicatePhoneError,
  SupportedPhoneCountryCode,
  startWhatsAppAuth,
  verifyWhatsAppAuth,
} from "../../auth/WhatsappAuth/whatsappAuthHelpers";
import WhatsAppPhoneInput from "../../auth/WhatsappAuth/WhatsAppPhoneInput";
import { rememberLoginReturnTo, clearLoginReturnTo, consumeLoginDestination, getCheckoutReturnState, peekLoginReturnTo } from "./loginReturn";
type ClaimPhoneConflictError = Error & {
  code?: string;
  status?: number;
};
const Login = () => {
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] =
    useState<SupportedPhoneCountryCode>("+265");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [validationTouched, setValidationTouched] = useState({
    email: false,
    phone: false,
    password: false,
    confirmPassword: false,
  });
  const [isWhatsAppMode, setIsWhatsAppMode] = useState(false);
  const [whatsAppCode, setWhatsAppCode] = useState("");
  const [whatsAppStep, setWhatsAppStep] = useState<"phone" | "code">("phone");
  const [isSendingWhatsAppCode, setIsSendingWhatsAppCode] = useState(false);
  const [isVerifyingWhatsAppCode, setIsVerifyingWhatsAppCode] = useState(false);
  const [whatsAppCodeExpiresAt, setWhatsAppCodeExpiresAt] = useState<
    string | null
  >(null);
  const [whatsAppExistingAccountFound, setWhatsAppExistingAccountFound] =
    useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const authRedirectCompletedRef = useRef(false);
  const authOperationRef = useRef(false);
  const { user } = useAuth();
  const { theme } = useCustomTheme();
  const isDark = theme?.name === "dark";

  // A forced sign-in remembers its own return URL; normal sign-in has none.
  useEffect(() => {
    rememberLoginReturnTo(location.search, location.state);
  }, [location.search, location.state]);

  useEffect(() => () => {
    // Abandoning login must not redirect a later, normal login to a stale page.
    // Keep the handoff through reloads and intermediate authentication pages.
    const path = window.location.pathname;
    if (
      !authRedirectCompletedRef.current &&
      !/^\/(?:login|complete-phone|verify-phone|whatsapp-complete-profile)(?:\/|$)/i.test(path)
    ) {
      clearLoginReturnTo();
    }
  }, []);

  const finishAuthentication = useCallback(() => {
    if (authRedirectCompletedRef.current) return;
    authRedirectCompletedRef.current = true;

    const destination = consumeLoginDestination(location.search, location.state);
    const checkoutState = getCheckoutReturnState(destination);
    navigate(destination, {
      replace: true,
      ...(checkoutState !== undefined ? { state: checkoutState } : {}),
    });
  }, [location.search, location.state, navigate]);
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);
  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          await createUserProfile(result.user);
          toast({
            title: "Facebook Sign-In",
            description: "Login successful!",
            status: "success",
          });
          finishAuthentication();
        }
      })
      .catch((error) => {
        toast({
          title: "Facebook Sign-In Failed",
          description: error.message,
          status: "error",
        });
      });
  }, [toast, finishAuthentication]);
  useEffect(() => {
    if (user && !isWhatsAppMode && !isSignup && !authOperationRef.current) {
      finishAuthentication();
    }
  }, [user, isWhatsAppMode, isSignup, finishAuthentication]);
  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  const getEmailValidationError = (value: string) => {
    const normalizedEmail = value.trim();
    if (!normalizedEmail) {
      return "Email address is required.";
    }
    if (!isValidEmail(normalizedEmail)) {
      return "Enter a valid email address.";
    }
    return "";
  };
  const getPasswordValidationError = (value: string) => {
    if (!value) {
      return "Password is required.";
    }
    if (value.length < 8) {
      return "Password must be at least 8 characters.";
    }
    return "";
  };
  const getConfirmPasswordValidationError = (
    value: string,
    currentPassword: string,
  ) => {
    if (!value) {
      return "Please confirm your password.";
    }
    if (value !== currentPassword) {
      return "Passwords do not match.";
    }
    return "";
  };
  const getPhoneValidationError = (
    selectedCountryCode: SupportedPhoneCountryCode,
    selectedPhoneNumber: string,
  ) => {
    const digits = selectedPhoneNumber.replace(/\D/g, "");
    const localDigits = digits.replace(/^0+/, "");
    if (!digits) {
      return "Phone number is required.";
    }
    if (selectedCountryCode === "+27") {
      if (localDigits.length !== 9 || !/^[6-8]\d{8}$/.test(localDigits)) {
        return "Enter a valid South African phone number.";
      }
    }
    if (selectedCountryCode === "+265") {
      if (
        localDigits.length < 8 ||
        localDigits.length > 9 ||
        !/^[189]\d{7,8}$/.test(localDigits)
      ) {
        return "Enter a valid Malawi phone number.";
      }
    }
    return "";
  };
  const handleSignupPhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, "");
    const maxLength = digits.startsWith("0") ? 10 : 9;
    const nextPhoneNumber = digits.slice(0, maxLength);
    setPhoneNumber(nextPhoneNumber);
    setValidationTouched((current) => ({
      ...current,
      phone: true,
    }));
    setValidationErrors((current) => ({
      ...current,
      phone: getPhoneValidationError(countryCode, nextPhoneNumber),
    }));
  };
  const cleanPhoneNumberForClaim = (
    selectedCountryCode: SupportedPhoneCountryCode,
    selectedPhoneNumber: string,
  ) => {
    const cleanCountryCode = selectedCountryCode.replace(/\D/g, "");
    const cleanPhone = selectedPhoneNumber
      .replace(/\D/g, "")
      .replace(/^0+/, "");
    if (!cleanCountryCode || !cleanPhone) {
      return "";
    }
    if (cleanPhone.startsWith(cleanCountryCode)) {
      return cleanPhone;
    }
    return `${cleanCountryCode}${cleanPhone}`;
  };
  const navigateToPhoneUpdatePage = (
    credential: UserCredential,
    attemptedPhoneNumber: string,
  ) => {
    const uid = encodeURIComponent(credential.user.uid);
    const phone = encodeURIComponent(attemptedPhoneNumber);
    const emailParam = encodeURIComponent(credential.user.email || email || "");
    const returnTo = peekLoginReturnTo(location.search, location.state);
    const returnParam = returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : "";
    navigate(
      `/complete-phone?uid=${uid}&phone=${phone}&email=${emailParam}&from=signup${returnParam}`,
    );
  };
  const handlePhoneClaimConflict = async (
    credential: UserCredential,
    attemptedPhoneNumber: string,
  ) => {
    try {
      await claimPhoneNumberViaBackend({
        currentUid: credential.user.uid,
        phoneNumber: attemptedPhoneNumber,
      });
      finishAuthentication();
      return;
    } catch (error) {
      const err = error as ClaimPhoneConflictError;
      if (err.code === "PHONE_ALREADY_IN_USE" || err.status === 409) {
        navigateToPhoneUpdatePage(credential, attemptedPhoneNumber);
        return;
      }
      console.error("Phone claim skipped due to network/backend error:", err);
      toast({
        title: "Account created",
        description:
          "Your account was created, but we could not verify the phone number right now.",
        status: "warning",
        duration: 5000,
      });
      finishAuthentication();
    }
  };
  const handleCountryChange = (value: SupportedPhoneCountryCode) => {
    setCountryCode(value);
    setPhoneNumber("");
    setValidationErrors((current) => ({
      ...current,
      phone: validationTouched.phone ? "Phone number is required." : "",
    }));
    setWhatsAppCode("");
    setWhatsAppStep("phone");
    setWhatsAppCodeExpiresAt(null);
    setWhatsAppExistingAccountFound(false);
  };
  const getWhatsAppErrorTitle = (message: string, fallback: string) => {
    if (isDuplicatePhoneError(message)) {
      return "Phone number already linked";
    }
    return fallback;
  };
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    authOperationRef.current = true;
    try {
      if (isSignup) {
        const normalizedEmail = email.trim();
        const nextValidationErrors = {
          email: getEmailValidationError(normalizedEmail),
          phone: getPhoneValidationError(countryCode, phoneNumber),
          password: getPasswordValidationError(password),
          confirmPassword: getConfirmPasswordValidationError(
            confirmPassword,
            password,
          ),
        };
        setValidationTouched({
          email: true,
          phone: true,
          password: true,
          confirmPassword: true,
        });
        setValidationErrors(nextValidationErrors);
        const firstValidationError =
          nextValidationErrors.email ||
          nextValidationErrors.phone ||
          nextValidationErrors.password ||
          nextValidationErrors.confirmPassword;
        if (firstValidationError) {
          throw new Error(firstValidationError);
        }
        if (!phoneNumber.trim()) {
          throw new Error("Phone number is required for signup.");
        }
        const fullPhone = cleanPhoneNumberForClaim(countryCode, phoneNumber);
        if (!fullPhone) {
          throw new Error("Please enter a valid phone number.");
        }
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        await createUserProfile(userCredential.user, {
          displayName:
            userCredential.user.displayName || email.split("@")[0] || "",
        });
        await handlePhoneClaimConflict(userCredential, fullPhone);
        toast({
          title: "Account created",
          description: "Welcome to the platform!",
          status: "success",
          duration: 4000,
        });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        toast({
          title: "Login successful",
          description: "Welcome back!",
          status: "success",
          duration: 4000,
        });
        finishAuthentication();
      }
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
    } catch (error) {
      toast({
        title: `${isSignup ? "Signup" : "Login"} failed`,
        description: (error as Error).message,
        status: "error",
        duration: 5000,
      });
    } finally {
      authOperationRef.current = false;
    }
  };
  const handleGoogleAuth = async () => {
    authOperationRef.current = true;
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      await createUserProfile(userCredential.user);
      toast({
        title: "Google Sign-In",
        description: "You are now logged in!",
        status: "success",
        duration: 4000,
      });
      finishAuthentication();
    } catch (error) {
      toast({
        title: "Google Sign-In Failed",
        description: (error as Error).message,
        status: "error",
        duration: 5000,
      });
    } finally {
      authOperationRef.current = false;
    }
  };
  const handleFacebookAuth = async () => {
    authOperationRef.current = true;
    try {
      const result = await signInWithPopup(auth, facebookProvider);
      await createUserProfile(result.user);
      toast({
        title: "Facebook Sign-In",
        description: "Login successful!",
        status: "success",
      });
      finishAuthentication();
    } catch (error) {
      toast({
        title: "Facebook Sign-In Failed",
        description: (error as Error).message,
        status: "error",
        duration: 5000,
      });
    } finally {
      authOperationRef.current = false;
    }
  };
  const handleResetPassword = async () => {
    if (!email) {
      toast({
        title: "Enter your email first",
        status: "info",
        duration: 3000,
      });
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      toast({
        title: "Reset email sent",
        description: "Check your inbox to reset your password.",
        status: "success",
        duration: 4000,
      });
    } catch (error) {
      toast({
        title: "Reset failed",
        description: (error as Error).message,
        status: "error",
        duration: 4000,
      });
    }
  };
  const handleLogout = async () => {
    try {
      await signOut(auth);
      clearLoginReturnTo();
      localStorage.removeItem("rememberedEmail");
      navigate("/login", { replace: true });
    } catch (error) {
      toast({
        title: "Logout failed",
        description: (error as Error).message,
        status: "error",
        duration: 4000,
      });
    }
  };
  const handleStartWhatsApp = async () => {
    try {
      const phoneValidationError = getPhoneValidationError(
        countryCode,
        phoneNumber,
      );
      setValidationTouched((current) => ({
        ...current,
        phone: true,
      }));
      setValidationErrors((current) => ({
        ...current,
        phone: phoneValidationError,
      }));
      if (phoneValidationError) {
        throw new Error(phoneValidationError);
      }
      setIsSendingWhatsAppCode(true);
      const result = await startWhatsAppAuth({
        countryCode,
        phoneNumber,
        intent: isSignup ? "signup" : "login",
      });
      setWhatsAppCode("");
      setWhatsAppStep("code");
      setWhatsAppCodeExpiresAt(result.expiresAt || null);
      setWhatsAppExistingAccountFound(Boolean(result.existingAccountFound));
      toast({
        title: result.existingAccountFound
          ? "Code sent to existing account"
          : "Code sent",
        description: result.existingAccountFound
          ? "This WhatsApp number is already linked to an account. Enter the latest code to sign in to that account."
          : result?.message ||
            "Check your WhatsApp for the latest verification code.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message || (error as Error).message;
      toast({
        title: getWhatsAppErrorTitle(message, "WhatsApp verification failed"),
        description: message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsSendingWhatsAppCode(false);
    }
  };
  const handleVerifyWhatsApp = async () => {
    try {
      if (!whatsAppCode.trim()) {
        throw new Error("Please enter the WhatsApp verification code.");
      }
      setIsVerifyingWhatsAppCode(true);
      const result = await verifyWhatsAppAuth({
        countryCode,
        phoneNumber,
        code: whatsAppCode,
      });
      const credential = await signInWithCustomToken(auth, result.customToken);
      console.log("✅ WhatsApp custom token sign-in success:", {
        uid: credential.user.uid,
        requiresProfileCompletion: result.requiresProfileCompletion,
        resultUser: result.user,
      });
      toast({
        title: "WhatsApp login successful",
        description: result.requiresProfileCompletion
          ? "You can complete your profile now or later."
          : "You are now signed in.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      const returnTo = peekLoginReturnTo(location.search, location.state);
      if (result.requiresProfileCompletion && !returnTo) {
        // Preserve the existing profile-completion experience for normal login.
        setTimeout(() => {
          navigate("/whatsapp-complete-profile", { replace: true });
        }, 300);
      } else {
        // A required login returns to the original checkout/action, not dashboard.
        setTimeout(() => {
          finishAuthentication();
        }, 300);
      }
    } catch (error) {
      console.error("❌ WhatsApp verify/login failed:", error);
      const message =
        (error as any)?.response?.data?.message || (error as Error).message;
      toast({
        title: getWhatsAppErrorTitle(message, "Code verification failed"),
        description: message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsVerifyingWhatsAppCode(false);
    }
  };
  const cardsBorderRadii = "8px";
  const cardText = isDark ? "rgba(255,255,255,0.92)" : "#0B1220";
  const labelText = isDark ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)";
  const subText = isDark ? "rgba(255,255,255,0.65)" : "rgba(15,23,42,0.60)";
  const dividerColor = isDark
    ? "rgba(255,255,255,0.10)"
    : "rgba(15,23,42,0.10)";
  const linkColor = isDark ? "#60A5FA" : "#2563EB";
  const cardShadow = isDark
    ? "0 18px 55px rgba(0,0,0,0.55)"
    : "0 18px 55px rgba(0,0,0,0.16)";
  const cardBg = isDark
    ? "linear-gradient(135deg, rgba(17,24,39,0.92), rgba(2,6,23,0.78), rgba(15,23,42,0.86))"
    : "linear-gradient(to-br, rgba(255,255,255,0.98), rgba(248,250,255,0.92), rgba(255,255,255,0.90))";
  const cardBorder = isDark
    ? "rgba(255,255,255,0.14)"
    : "rgba(255,255,255,0.65)";
  const inputStyles = {
    bg: isDark ? "rgba(2,6,23,0.35)" : "rgba(255,255,255,0.85)",
    color: cardText,
    borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(15,23,42,0.10)",
    _placeholder: {
      color: isDark ? "rgba(255,255,255,0.45)" : "rgba(15,23,42,0.35)",
    },
    _hover: {
      borderColor: isDark ? "rgba(255,255,255,0.26)" : "rgba(15,23,42,0.18)",
    },
    _focus: {
      borderColor: "#3B82F6",
      boxShadow: isDark
        ? "0 0 0 4px rgba(59,130,246,0.22)"
        : "0 0 0 4px rgba(59,130,246,0.15)",
    },
  } as const;
  const primaryAuthButton = isSignup
    ? {
        bgGradient: isDark
          ? "linear(to-r, #319795, #4FD1C5)"
          : "linear(to-r, #38B2AC, #81E6D9)",
        boxShadow: isDark
          ? "0 12px 24px rgba(49,151,149,0.26)"
          : "0 12px 24px rgba(56,178,172,0.20)",
      }
    : {
        bgGradient: isDark
          ? "linear(to-r, #0B1220, #111827, #0B1220)"
          : "linear(to-r, #0F172A, #111827, #0B1220)",
        boxShadow: isDark
          ? "0 12px 24px rgba(0,0,0,0.40)"
          : "0 12px 24px rgba(15,23,42,0.18)",
      };
  return (
    <Container maxW="500px" p={{ base: "0", md: "30px" }}>
      <Box
        p={8}
        borderWidth="1px"
        borderRadius={{ base: "0", md: cardsBorderRadii }}
        boxShadow={cardShadow}
        bgGradient={cardBg}
        borderColor={cardBorder}
        backdropFilter="blur(14px)"
        color={cardText}
        minH={{ base: "100vh", md: "100%" }}
        overflow="hidden"
        position="relative"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="5px"
          opacity={0.95}
        />
        <VStack spacing={6} position="relative">
          <Heading width="100%" textAlign="left" size="lg" color={cardText}>
            {isWhatsAppMode
              ? whatsAppStep === "phone"
                ? "Continue with WhatsApp"
                : "Enter WhatsApp Code"
              : isSignup
                ? "Create an Account"
                : "Sign In"}
          </Heading>
          {!isWhatsAppMode ? (
            <form
              onSubmit={handleAuth}
              style={{ width: "100%" }}
              noValidate={isSignup}
            >
              <Stack spacing={4}>
                <FormControl
                  isRequired
                  isInvalid={
                    isSignup &&
                    validationTouched.email &&
                    Boolean(validationErrors.email)
                  }
                >
                  <FormLabel color={labelText}>Email address</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const nextEmail = e.target.value;
                      setEmail(nextEmail);
                      if (isSignup) {
                        setValidationTouched((current) => ({
                          ...current,
                          email: true,
                        }));
                        setValidationErrors((current) => ({
                          ...current,
                          email: getEmailValidationError(nextEmail),
                        }));
                      }
                    }}
                    {...inputStyles}
                  />
                  {isSignup && (
                    <FormErrorMessage>
                      {validationErrors.email}
                    </FormErrorMessage>
                  )}
                </FormControl>
                {isSignup && (
                  <WhatsAppPhoneInput
                    isRequired
                    label="Phone number"
                    countryCode={countryCode}
                    phoneNumber={phoneNumber}
                    onCountryCodeChange={handleCountryChange}
                    onPhoneNumberChange={handleSignupPhoneChange}
                    labelColor={labelText}
                    inputStyles={inputStyles}
                  />
                )}
                {isSignup &&
                  validationTouched.phone &&
                  validationErrors.phone && (
                    <Text mt="-2" fontSize="sm" color="red.500">
                      {validationErrors.phone}
                    </Text>
                  )}
                <FormControl
                  isRequired
                  isInvalid={
                    isSignup &&
                    validationTouched.password &&
                    Boolean(validationErrors.password)
                  }
                >
                  <FormLabel color={labelText}>Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        const nextPassword = e.target.value;
                        setPassword(nextPassword);
                        if (isSignup) {
                          setValidationTouched((current) => ({
                            ...current,
                            password: true,
                          }));
                          setValidationErrors((current) => ({
                            ...current,
                            password: getPasswordValidationError(nextPassword),
                            confirmPassword: validationTouched.confirmPassword
                              ? getConfirmPasswordValidationError(
                                  confirmPassword,
                                  nextPassword,
                                )
                              : current.confirmPassword,
                          }));
                        }
                      }}
                      {...inputStyles}
                    />
                    <InputRightElement h="full">
                      <IconButton
                        variant="ghost"
                        aria-label="Toggle password"
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {isSignup && (
                    <FormErrorMessage>
                      {validationErrors.password}
                    </FormErrorMessage>
                  )}
                </FormControl>
                {isSignup && (
                  <FormControl
                    isRequired
                    isInvalid={
                      validationTouched.confirmPassword &&
                      Boolean(validationErrors.confirmPassword)
                    }
                  >
                    <FormLabel color={labelText}>Confirm password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          const nextConfirmPassword = e.target.value;
                          setConfirmPassword(nextConfirmPassword);
                          setValidationTouched((current) => ({
                            ...current,
                            confirmPassword: true,
                          }));
                          setValidationErrors((current) => ({
                            ...current,
                            confirmPassword: getConfirmPasswordValidationError(
                              nextConfirmPassword,
                              password,
                            ),
                          }));
                        }}
                        {...inputStyles}
                      />
                      <InputRightElement h="full">
                        <IconButton
                          variant="ghost"
                          aria-label="Toggle confirm password"
                          icon={
                            showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                          }
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>
                      {validationErrors.confirmPassword}
                    </FormErrorMessage>
                  </FormControl>
                )}
                {!isSignup && (
                  <HStack justifyContent="space-between">
                    <Checkbox
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      colorScheme="blue"
                    >
                      Remember me
                    </Checkbox>
                    <Link
                      color={linkColor}
                      fontSize="sm"
                      onClick={handleResetPassword}
                    >
                      Forgot password?
                    </Link>
                  </HStack>
                )}
                <Button
                  {...primaryAuthButton}
                  _hover={{
                    filter: "brightness(1.06)",
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  fontWeight="bold"
                  color="white"
                  type="submit"
                  width="full"
                  borderRadius={cardsBorderRadii}
                >
                  {isSignup ? "Sign Up" : "Sign In"}
                </Button>
              </Stack>
            </form>
          ) : (
            <Stack spacing={4} width="100%">
              <WhatsAppPhoneInput
                isRequired
                label="WhatsApp number"
                countryCode={countryCode}
                phoneNumber={phoneNumber}
                onCountryCodeChange={handleCountryChange}
                onPhoneNumberChange={handleSignupPhoneChange}
                labelColor={labelText}
                inputStyles={inputStyles}
              />
              {whatsAppStep === "phone" &&
                validationTouched.phone &&
                validationErrors.phone && (
                  <Text mt="-2" fontSize="sm" color="red.500">
                    {validationErrors.phone}
                  </Text>
                )}
              {whatsAppStep === "code" && (
                <>
                  <FormControl isRequired>
                    <FormLabel color={labelText}>Verification code</FormLabel>
                    <Input
                      type="text"
                      value={whatsAppCode}
                      onChange={(e) =>
                        setWhatsAppCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      placeholder="Enter latest 6-digit code"
                      {...inputStyles}
                    />
                  </FormControl>
                  <Stack spacing={1}>
                    {whatsAppExistingAccountFound && (
                      <Text fontSize="sm" color={subText}>
                        This number is already linked to an account. Enter the
                        latest code to sign in to that account.
                      </Text>
                    )}
                    {whatsAppCodeExpiresAt && (
                      <Text fontSize="xs" color={subText}>
                        Code expires at{" "}
                        {new Date(whatsAppCodeExpiresAt).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                        .
                      </Text>
                    )}
                  </Stack>
                </>
              )}
              {whatsAppStep === "phone" ? (
                <Button
                  leftIcon={<Icon color="teal.700" as={FaWhatsapp as any} />}
                  colorScheme="whatsapp"
                  onClick={handleStartWhatsApp}
                  isLoading={isSendingWhatsAppCode}
                  isDisabled={
                    isSendingWhatsAppCode ||
                    isVerifyingWhatsAppCode ||
                    Boolean(getPhoneValidationError(countryCode, phoneNumber))
                  }
                  borderRadius="5px"
                  color={cardText}
                  variant="outline"
                >
                  Send WhatsApp Code
                </Button>
              ) : (
                <Stack spacing={3}>
                  <Button
                    leftIcon={<Icon color="teal.600" as={FaWhatsapp as any} />}
                    colorScheme="whatsapp"
                    onClick={handleVerifyWhatsApp}
                    isLoading={isVerifyingWhatsAppCode}
                    isDisabled={
                      isSendingWhatsAppCode ||
                      isVerifyingWhatsAppCode ||
                      whatsAppCode.length !== 6
                    }
                    borderRadius="5px"
                    color={cardText}
                    variant="outline"
                  >
                    Verify and Sign In
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleStartWhatsApp}
                    isLoading={isSendingWhatsAppCode}
                    isDisabled={
                      isSendingWhatsAppCode || isVerifyingWhatsAppCode
                    }
                    color={linkColor}
                  >
                    Send New WhatsApp Code
                  </Button>
                </Stack>
              )}
              <Button
                variant="ghost"
                onClick={() => {
                  if (whatsAppStep === "code") {
                    setWhatsAppStep("phone");
                    setWhatsAppCode("");
                    setWhatsAppCodeExpiresAt(null);
                    setWhatsAppExistingAccountFound(false);
                    return;
                  }
                  setIsWhatsAppMode(false);
                }}
              >
                Back
              </Button>
            </Stack>
          )}
          {!isWhatsAppMode && (
            <Text
              color={isDark ? "rgba(255,255,255,0.70)" : "rgba(15,23,42,0.75)"}
            >
              {isSignup ? "Already have an account?" : "Don't have an account?"}{" "}
              <Button
                onClick={() => setIsSignup(!isSignup)}
                variant="link"
                color={linkColor}
                size="sm"
              >
                {isSignup ? "Sign In" : "Sign Up"}
              </Button>
            </Text>
          )}
          <Divider borderColor={dividerColor} />
          {!isWhatsAppMode && <Text color={subText}>or continue with</Text>}
          <Stack spacing={4} width="100%">
            {!isWhatsAppMode && (
              <>
                <Button
                  leftIcon={
                    <Icon
                      as={FaWhatsapp as any}
                      boxSize={5}
                      borderRadius={cardsBorderRadii}
                      color="#1F8F4D"
                    />
                  }
                  variant="outline"
                  bg="transparent"
                  color="#1F8F4D"
                  border="1px solid"
                  borderColor="#1F8F4D"
                  h="40px"
                  minH="40px"
                  px={4}
                  fontWeight="600"
                  borderRadius={cardsBorderRadii}
                  _hover={{
                    bg: "rgba(31, 143, 77, 0.06)",
                    borderColor: "#187A41",
                    color: "#187A41",
                  }}
                  _active={{
                    bg: "rgba(31, 143, 77, 0.10)",
                    borderColor: "#146B39",
                    color: "#146B39",
                  }}
                  onClick={() => {
                    setIsWhatsAppMode(true);
                    setWhatsAppStep("phone");
                    setWhatsAppCode("");
                    setWhatsAppCodeExpiresAt(null);
                    setWhatsAppExistingAccountFound(false);
                    setCountryCode("+265");
                    setPhoneNumber("");
                    setValidationTouched((current) => ({
                      ...current,
                      phone: false,
                    }));
                    setValidationErrors((current) => ({
                      ...current,
                      phone: "",
                    }));
                  }}
                >
                  Continue with WhatsApp
                </Button>
                <Button
                  variant="outline"
                  leftIcon={FcGoogle({}) as React.ReactElement}
                  onClick={handleGoogleAuth}
                >
                  Continue with Google
                </Button>
                <Button onClick={handleFacebookAuth}>
                  <Icon
                    as={FaFacebook as React.ElementType}
                    boxSize={4}
                    mr="8px"
                    color="#FFF"
                  />
                  Continue with Facebook
                </Button>
              </>
            )}
            {user ? (
              <Button variant="ghost" onClick={handleLogout}>
                Logout
              </Button>
            ) : null}
          </Stack>
        </VStack>
      </Box>
    </Container>
  );
};
export default Login;
